/**
 * UI adapter for CTR Goal % while keeping backend contract unchanged.
 *
 * Backend stores `impression_rate` as: impressions / (impressions + clicks) * 100.
 * UI wants CTR Goal % as: clicks / impressions * 100.
 *
 * Conversion:
 * - CTR p -> impression_rate r: r = 10000 / (100 + p)
 * - impression_rate r -> CTR p: p = 10000 / r - 100
 *
 * Special value:
 * -1 means "no override / use publisher default" and must pass through unchanged.
 */

function toNum(v) {
	// Normalize strings/numbers coming from v-model and API.
	const n = typeof v === "string" ? parseFloat(v) : Number(v);
	return Number.isFinite(n) ? n : NaN;
}

function clamp2(v) {
	// Keep UI stable: at most 2 decimals.
	return parseFloat(Number(v).toFixed(2));
}

export function impressionRateToCtrGoal(impressionRate) {
	// Preserve "no override".
	const r = toNum(impressionRate);
	if (r === -1) return -1;

	// Guard rails: avoid division by 0 and obvious invalids.
	if (!Number.isFinite(r) || r <= 0 || r > 100) return 0;

	// Convert to CTR Goal.
	const p = 10000 / r - 100;
	return clamp2(p);
}

export function ctrGoalToImpressionRate(ctrGoal) {
	// Preserve "no override".
	const p = toNum(ctrGoal);
	if (p === -1) return -1;

	// Guard rails: keep within reasonable bounds for a goal %.
	if (!Number.isFinite(p) || p < 0) return 0;

	// Convert back to backend ratio.
	const r = 10000 / (100 + p);
	return clamp2(r);
}

export function calculateDerivedImpressionCap(clickCap, impressionRate) {
	const c = toNum(clickCap);
	const r = toNum(impressionRate);

	if (!Number.isFinite(r) || r < 0 || r >= 100) {
		return null;
	}
	if (!Number.isFinite(c) || c <= 0 || r === 0) {
		return 0;
	}

	return Math.round(c * (r / (100 - r)));
}

/** A configured rate derives the daily impression cap only when auto calculation is enabled. */
export function isImprRateCapDerived(impressionRate, autoCalculation) {
	if (!autoCalculation) return false;
	const r = toNum(impressionRate);
	return Number.isFinite(r) && r !== -1 && r !== 100;
}

/**
 * Decide what happens to the daily impression cap when impression_rate changes.
 *
 * The rate is edited keystroke by keystroke (typing 100 passes through 1 and 10), and every
 * derived rate overwrites the stored cap. So the operator's own cap is remembered the moment
 * the rate enters the derived range and put back when the rate leaves it again (-1 or 100),
 * instead of leaving the cap at whatever the last intermediate keystroke produced.
 *
 * `previousRate` is undefined on the first evaluation (component mount): a derived initial
 * rate then snapshots the cap as loaded, so a later switch to 100% / "no change" restores it.
 *
 * Returns `impressionCap` to apply (null = leave the field alone) and the snapshot to keep.
 */
export function resolveImpressionCapOnRateChange({
	previousRate,
	nextRate,
	clickCap,
	currentImpressionCap,
	manualImpressionCap,
}) {
	const wasDerived = previousRate !== undefined && isImprRateCapDerived(previousRate, true);

	if (isImprRateCapDerived(nextRate, true)) {
		return {
			impressionCap: calculateDerivedImpressionCap(clickCap, nextRate),
			manualImpressionCap: wasDerived ? manualImpressionCap : currentImpressionCap,
		};
	}

	const hasSnapshot = manualImpressionCap !== null && manualImpressionCap !== undefined;
	return {
		impressionCap: wasDerived && hasSnapshot ? manualImpressionCap : null,
		manualImpressionCap: null,
	};
}

export function isImpressionRateActive(impressionRate) {
	return toNum(impressionRate) !== -1;
}

export function shouldShowMissingClickCapWarning(clickCap, impressionRate) {
	const rate = toNum(impressionRate);
	return clickCap !== null
		&& clickCap !== undefined
		&& rate !== -1
		&& rate !== 100
		&& !(+clickCap > 0);
}

export function applyDerivedPlacementImpressionCap(placement, impressionCap) {
	if (placement.is_cr_optimized) {
		return false;
	}

	placement.daily_impression_cap = impressionCap;
	return true;
}
